import React from 'react';

import { Button, Grid, List, ListItem, ListItemIcon, Spacer, SxProps, Typography } from '@emburse/ui-core';
import { CheckIcon, Icon } from '@emburse/ui-essentials';

import { useActivationPlanPricing } from '@ReactHooks/useActivationPlanPricing';
import { SPEND_BILLING_PLAN_TYPE } from '@Shared/constants';
import { getActivationPlanMinimumSummary, getActivationPlanPerUserAmount } from '@Shared/helpers';
import { ICompanyModel } from '@Shared/models';

import * as style from './SignUpPlanSelection.styl';

interface ISignUpSpendPlans {
  handleSelectPlan: (billing_plan: ICompanyModel['billing_plan']) => void;
  basicButtonText?: string;
  disableBasicButton?: boolean;
  availablePlans?: string[];
  isMigration?: boolean;
  partnerId?: string;
}

export const ListItemWithCheckbox: React.FC<{ sx?: SxProps; children?: React.ReactNode }> = ({ children, sx }) => (
  <ListItem sx={sx}>
    <ListItemIcon>
      <Icon iconUrl={CheckIcon} />
    </ListItemIcon>
    {children}
  </ListItem>
);

export const SignUpSpendPlans: React.FC<ISignUpSpendPlans> = ({
  handleSelectPlan,
  basicButtonText = 'Select',
  disableBasicButton = false,
  availablePlans = [SPEND_BILLING_PLAN_TYPE.BASIC_PLAN, SPEND_BILLING_PLAN_TYPE.PRO_PLAN],
  isMigration = false
}) => {
  const planCardSize = availablePlans.length === 1 ? 12 : 6;
  const currencyCode = window.ORG?.currency_code || 'USD';
  const { pricingDetails: basicPlanPricing, pricingLoading: basicPlanPricingLoading } = useActivationPlanPricing(SPEND_BILLING_PLAN_TYPE.BASIC_PLAN);
  const { pricingDetails: plusPlanPricing, pricingLoading: plusPlanPricingLoading } = useActivationPlanPricing(SPEND_BILLING_PLAN_TYPE.PRO_PLAN);

  const basicPlanPrice = getActivationPlanPerUserAmount(basicPlanPricing, currencyCode);
  const basicPlanMinimumCopy = getActivationPlanMinimumSummary(basicPlanPricing, currencyCode);
  const plusPlanPrice = getActivationPlanPerUserAmount(plusPlanPricing, currencyCode);
  const plusPlanMinimumCopy = getActivationPlanMinimumSummary(plusPlanPricing, currencyCode);

  return (
    <Grid container direction={'row'} spacing={2} className={style.global}>
      {availablePlans.includes(SPEND_BILLING_PLAN_TYPE.BASIC_PLAN) && (
        <Grid item sm={planCardSize}>
          <div className="cardStyle">
            <Typography variant="h5">Basic</Typography>
            <Spacer size={24} />
            {basicPlanPricingLoading ? (
              <Typography variant="body2">Loading pricing...</Typography>
            ) : (
              <Typography variant="h6">
                {basicPlanPrice || 'Pricing unavailable'}
                {basicPlanPrice && <Typography variant="caption">/user per month</Typography>}
              </Typography>
            )}
            <Spacer size={8} />
            {basicPlanMinimumCopy && <Typography variant="body2">{basicPlanMinimumCopy}</Typography>}
            <Spacer size={24} />
            <div className="flex jc-c">
              <Button
                style={{ width: '100%' }}
                disabled={disableBasicButton}
                onClick={() => handleSelectPlan('spend_basic_eight_dollars')}
                data-testid="spend_basic_eight"
                className="spend-basic"
              >
                {basicButtonText}
              </Button>
              {!isMigration && (
                <>
                  <Spacer size={8} />
                  <Typography variant="body2">(Includes free 30 day trial)</Typography>
                </>
              )}
            </div>
            <Spacer size={24} />
            <List>
              <ListItemWithCheckbox>Emburse Cards or select partner virtual card issuance</ListItemWithCheckbox>
              <ListItemWithCheckbox>Corporate card feed integration</ListItemWithCheckbox>
              <ListItemWithCheckbox>Accounting Integration with Quickbooks Online, Quickbooks Desktop and Xero</ListItemWithCheckbox>
              <ListItemWithCheckbox>50 monthly ACH reimbursements</ListItemWithCheckbox>
              <ListItemWithCheckbox>Unlimited, advanced policy and approval controls</ListItemWithCheckbox>
              <ListItemWithCheckbox>Personalized Expenses Export Templates</ListItemWithCheckbox>
              <ListItemWithCheckbox>Slack Integration</ListItemWithCheckbox>
              <ListItemWithCheckbox>Security Features: SAML SSO and 2FA</ListItemWithCheckbox>
              <ListItemWithCheckbox>Self-guided implementation</ListItemWithCheckbox>
            </List>
          </div>
        </Grid>
      )}

      {availablePlans.includes(SPEND_BILLING_PLAN_TYPE.PRO_PLAN) && (
        <Grid item sm={planCardSize}>
          <div className="cardStyle">
            <Typography variant="h5">Plus</Typography>
            <Spacer size={24} />
            {plusPlanPricingLoading ? (
              <Typography variant="body2">Loading pricing...</Typography>
            ) : (
              <Typography variant="h6">
                {plusPlanPrice || 'Pricing unavailable'}
                {plusPlanPrice && <Typography variant="caption">/user per month</Typography>}
              </Typography>
            )}
            <Spacer size={8} />
            {plusPlanMinimumCopy && <Typography variant="body2">{plusPlanMinimumCopy}</Typography>}
            <Spacer size={24} />
            <div className="flex jc-c">
              <Button
                style={{ width: '100%' }}
                onClick={() => handleSelectPlan('spend_plus_twelve_dollars')}
                data-testid="spend_plus_twelve"
                className="spend-plus"
              >
                Select
              </Button>
              {!isMigration && (
                <>
                  <Spacer size={8} />
                  <Typography variant="body2">(Includes free 30 day trial)</Typography>
                </>
              )}
            </div>
            <Spacer size={24} />
            <List>
              <ListItem sx={{ fontWeight: 600 }}>
                <ListItemIcon>
                  <Icon iconUrl={CheckIcon} />
                </ListItemIcon>
                Everything in Basic, +
              </ListItem>
              <ListItemWithCheckbox>More accounting integrations: NetSuite and Sage Intacct</ListItemWithCheckbox>
              <ListItemWithCheckbox>Unlimited ACH reimbursements</ListItemWithCheckbox>
              <ListItemWithCheckbox>Enhanced Roles (Manager, Accounting)</ListItemWithCheckbox>
              <ListItemWithCheckbox>Bill Pay (Virtual Card payments only)</ListItemWithCheckbox>
              <ListItemWithCheckbox>Salesforce API</ListItemWithCheckbox>
              <ListItemWithCheckbox>Amazon Business Integration</ListItemWithCheckbox>
              <ListItemWithCheckbox>Multi-Subsidiary Support* (up to 5 subsidiaries**)</ListItemWithCheckbox>
              <ListItemWithCheckbox>SFTP Accounting Automation*</ListItemWithCheckbox>
              <ListItemWithCheckbox>REST API Access*</ListItemWithCheckbox>
            </List>
            <Typography variant="caption">*Requires guided implementation</Typography>
            <Spacer size={8} />
            <Typography variant="caption">**Each subsidiary is subject to minimum billing requirements</Typography>
          </div>
        </Grid>
      )}
    </Grid>
  );
};
